import { Dropdown, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Button } from '../Button'
import { ErrorComponent } from '../ErrorComponent'
import { Loading } from '../Loading'
import { TeamMemberCard } from '../TeamMemberCard/TeamMemberCard'
import { useGetSearchParam } from '@/hooks'

type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']
type MessagesSubscriptionsOrganisationTeamUser = RestSchemas['Messages.Subscriptions.OrganisationTeamUser']
type MessagesSubscriptionsOrganisationTeam = RestSchemas['Messages.Subscriptions.OrganisationTeam']

export const MaintainProjectTeam = ({
  selectedProjectTeam,
}: {
  selectedProjectTeam?: MessagesSubscriptionsOrganisationTeam
}) => {
  const [projectTeamUsers, setProjectTeamUsers] = useState<MessagesSubscriptionsOrganisationUser[]>([])
  const [currentTeamUsers, setCurrentTeamUsers] = useState<MessagesSubscriptionsOrganisationTeamUser[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

  const orgId = useGetSearchParam<number>('orgId') ?? selectedProjectTeam?.organisationId ?? 0

  const queryClient = useQueryClient()

  const {
    getSubscriptionsApiListOrganisationUsers,
    getSubscriptionsApiListTeamUsers,
    postSubscriptionsApiAddTeamUser,
    deleteSubscriptionsApiDeleteTeamUser,
    getSubscriptionsApiGetOrganisationTeams,
  } = createRestClient()

  // This is the entire list project team users for the current org. Spread across various apps
  const {
    data: projectTeamUsersData = [],
    isPending: isProjectTeamUsersPending,
    isError: isProjectTeamUsersError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery<MessagesSubscriptionsOrganisationUser[]>({
    query: { orgId: orgId ?? selectedProjectTeam?.organisationId ?? 0 },
  })

  const {
    data: currentTeamUsersData = [],
    isPending: isCurrentTeamUsersPending,
    isError: isCurrentTeamUsersError,
  } = getSubscriptionsApiListTeamUsers.useQuery<MessagesSubscriptionsOrganisationTeamUser[]>(
    { query: { teamId: selectedProjectTeam?.id } },
    {
      select: (data) =>
        data.filter(
          (user) =>
            user.userId && user.userId !== 0 && user.id && user.id !== 0 && user.teamId === selectedProjectTeam?.id
        ),
      enabled: !!selectedProjectTeam?.id,
    }
  )

  // Initialize State when data loads
  useEffect(() => {
    setProjectTeamUsers(projectTeamUsersData)
    setCurrentTeamUsers(currentTeamUsersData)
  }, [projectTeamUsersData, currentTeamUsersData])

  const invalidateCurrentTeamUsers = () =>
    getSubscriptionsApiListTeamUsers.invalidateQueries(
      { parameters: { query: { teamId: selectedProjectTeam?.id } } },
      queryClient
    )

  const invalidateProjectTeamUsers = () =>
    getSubscriptionsApiListOrganisationUsers.invalidateQueries(
      {
        parameters: {
          query: { orgId: orgId ?? selectedProjectTeam?.organisationId ?? 0 },
        },
      },
      queryClient
    )

  const invalidateProjectTeams = () =>
    getSubscriptionsApiGetOrganisationTeams.invalidateQueries(
      { parameters: { query: { orgId: selectedProjectTeam?.organisationId || 0 } } },
      queryClient
    )

  const invalidateQueries = () => {
    invalidateProjectTeams()
    invalidateProjectTeamUsers()
    invalidateCurrentTeamUsers()
  }

  const {
    mutateAsync: addTeamUser,
    isPending: isAddTeamUserPending,
    isError: isAddTeamUserError,
  } = postSubscriptionsApiAddTeamUser.useMutation()

  const {
    mutate: deleteTeamUser,
    isPending: isDeleteTeamUserPending,
    isError: isDeleteTeamUserError,
  } = deleteSubscriptionsApiDeleteTeamUser.useMutation()

  const onOptionSelect = (_: SelectionEvents, data: OptionOnSelectData) => {
    setSelectedUserIds(data.selectedOptions)
  }

  const onAddSubmit = async () => {
    const selectedUsers = projectTeamUsers.filter((user) => selectedUserIds.includes(user.id.toString()))

    await Promise.all(
      selectedUsers.map((member) =>
        addTeamUser(
          {
            body: {
              userId: member.id,
              teamId: selectedProjectTeam?.id ?? 0,
              ...member,
              applicationId: selectedProjectTeam?.applicationId ?? 0,
              profilePicture: 'No profile pic submitted',
              teamName: 'team',
              organisationId: orgId,
              projectName: 'project',
              application: 'app',
              organisation: 'org',
              typeName: 'project',
              linkedProjectCount: 0,
              activeLinkedProjectCount: 0,
              linkedRoleCount: 0,
            },
          },
          {
            onError: (error) => {
              toast.error('Error adding team member')
              console.error(error)
            },
            onSuccess: () => {
              toast.success('Team member added')
            },
          }
        )
      )
    )

    setSelectedUserIds([])
    invalidateQueries()
  }

  const onDelete = (member: MessagesSubscriptionsOrganisationTeamUser) => {
    deleteTeamUser(
      {
        body: {
          ...member,
          teamId: selectedProjectTeam?.id ?? 0,
        },
      },
      {
        onError: (error) => {
          toast.error('Error deleting team member')
          console.error(error)
        },
        onSuccess: () => {
          toast.success('Team member deleted')
        },
        onSettled: () => {
          setSelectedUserIds([])
          invalidateQueries()
        },
      }
    )
  }

  if (isProjectTeamUsersPending || isAddTeamUserPending || isCurrentTeamUsersPending || isDeleteTeamUserPending)
    return <Loading />

  if (isProjectTeamUsersError || isCurrentTeamUsersError || isAddTeamUserError || isDeleteTeamUserError)
    return <ErrorComponent />

  const selectedUsers = projectTeamUsers.filter((user) => selectedUserIds.includes(user.id.toString()))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
      <Dropdown
        aria-labelledby={'add-project-user-to-team'}
        multiselect
        placeholder="Add project team members"
        selectedOptions={selectedUserIds}
        value={selectedUsers.map((u) => `${u.givenName ?? ''} ${u.familyName ?? ''}`.trim()).join(', ')}
        onOptionSelect={onOptionSelect}
      >
        <div style={{ maxHeight: '600px', overflowY: 'scroll' }}>
          {projectTeamUsers.map((option) => (
            <Option
              key={option.id}
              value={option.id.toString()}
              disabled={currentTeamUsers.some(
                (user) => user.userId === option.id && user.teamId === selectedProjectTeam?.id
              )}
            >
              {option.givenName || option.familyName
                ? `${option.givenName ?? ''} ${option.familyName ?? ''}`.trim()
                : (option.email ?? 'Unknown User')}
            </Option>
          ))}
        </div>
      </Dropdown>

      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
        <Button text="Add members" onClick={onAddSubmit} />
      </div>

      {currentTeamUsers?.map((user) => (
        <TeamMemberCard
          key={`${user.id}-${user.teamId}-${user.userId}`}
          onDelete={() => onDelete(user)}
          userId={user.userId}
          teamUsers={currentTeamUsers}
          teamId={user.teamId}
          teamName={user.teamName}
        />
      ))}
    </div>
  )
}
